<!-- Maintain_Users.vue -->
<template>
  <v-container class="py-6">
    <!-- ADD THIS SNACKBAR -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="5000"
      top
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="showSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
    <!-- END SNACKBAR -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="8">
        <v-alert type="info" variant="tonal" density="comfortable">
           @click:row="handleRowClick"
          These are current user accounts for <b>{{ currentUserOrgName || '...' }}</b>.
          You can add/remove roles and manage active roles. Users can switch between assigned roles.
          <br>
          <small><strong>💡 Tip:</strong> Click on any user row to fix their Firebase claims | <strong>Total Users:</strong> {{ rows.length }} | <strong>With Roles:</strong> {{ rows.filter(r => r.HasRoles).length }}</small>
        </v-alert>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn color="purple" @click="testCloudFunctionAuth">Test Auth</v-btn>
          <v-btn color="orange" @click="fixAllUserClaims" :loading="fixingClaims" class="ml-2">
            <v-icon start>mdi-account-cog</v-icon>
            Fix All Claims
          </v-btn>
          <v-btn color="red" @click="emergencyFixAllClaims" :loading="emergencyFixing" class="ml-2">
            <v-icon start>mdi-emergency</v-icon>
            Emergency Fix
          </v-btn>
      </v-col>
    </v-row>

    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
        ADMIN Maintain Users
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          size="small"
          @click="loadUsers"
          :loading="tableLoading"
          prepend-icon="mdi-refresh"
        >
          Refresh
        </v-btn>
      </v-card-title>

      <v-data-table
        fixed-header
        height="65vh"
        :headers="headers"
        :items="rows"
        :items-per-page="rows.length || 1000"
        hide-default-footer
        :loading="tableLoading"
        item-key="rowKey"
        class="elevation-1 text-start"
        density="comfortable"
        
      > 
        <template v-slot:[`item.NameLast`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" class="mr-3" :color="item.HasRoles ? 'primary' : 'grey'">
              <v-icon color="white">{{ item.HasRoles ? 'mdi-account-check' : 'mdi-account' }}</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">
                   {{ [item.NameFirst, item.NameLast].filter(Boolean).join(' ') || 'Unknown' }}
                   <v-chip 
                     v-if="fixingUser === item.UserID" 
                     size="x-small" 
                     color="orange" 
                     class="ml-2"
                   >
                     Fixing...
                   </v-chip>
                 </div>
              <div class="text-caption text-medium-emphasis">{{ item.Email }}</div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.Roles`]="{ item }">
          <div class="d-flex flex-wrap gap-1">
            <template v-if="item.HasRoles">
              <v-chip
                v-for="role in item.RolesList"
                :key="role"
                size="small"
                label
                :variant="role === item.ActiveRole ? 'flat' : 'outlined'"
                :color="role === item.ActiveRole ? 'green' : 'green'"
                :style="role === item.ActiveRole ? '' : 'border: 2px solid green;'"
                :prepend-icon="getRoleIcon(role)"
              >
                {{ role }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip
                color="orange"
                size="small"
                variant="outlined"
              >
                No roles
              </v-chip>
            </template>
            <v-chip
              v-if="item.RoleSource === 'claims'"
              color="warning"
              size="x-small"
              variant="flat"
            >
              <v-icon start size="x-small">mdi-alert</v-icon>
              Claims only
            </v-chip>
          </div>
        </template>

        <template v-slot:[`item.claims`]="{ item }">
          <div class="d-flex flex-wrap gap-1">
            <template v-if="item.ClaimsState === 'set'">
              <v-chip
                v-for="claim in item.ClaimsList"
                :key="claim"
                size="small"
                label
                variant="outlined"
                color="green"
                style="border: 2px solid green;"
                @click="showClaimsDialog(item)"
                class="clickable-claim"
              >
                {{ claim }}
              </v-chip>
            </template>
            <template v-else-if="item.ClaimsState === 'missing'">
              <v-chip
                size="small"
                label
                variant="flat"
                color="red"
                prepend-icon="mdi-alert-circle"
                @click="showClaimsDialog(item)"
                class="clickable-claim"
              >
                Claims Missing
              </v-chip>
            </template>
            <template v-else>
              <span class="text-caption text-grey clickable-claim" @click="showClaimsDialog(item)">—</span>
            </template>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex gap-1">
            <v-btn @click="openRoleModalForUser(item)"
              size="small" variant="text" color="primary" prepend-icon="mdi-account-plus">
              Add Role</v-btn>
            <v-btn v-if="item.RoleSource === 'claims'" @click="syncClaimsToFirestore(item.UserID)"
              size="small" variant="text" color="warning" prepend-icon="mdi-sync">
              Sync Claims</v-btn>
            
            <template v-if="item.HasRoles">
              <!-- Show specific role removal buttons -->
              <v-btn v-if="item.ActiveRole === 'RefereeCoach'" @click="confirmRemoveSpecificRole(item, 'RefereeCoach')"
                size="small" variant="text" color="error" prepend-icon="mdi-account-minus" >
                Remove RefereeCoach</v-btn>
              
              <v-btn
                v-if="item.ActiveRole === 'ADMIN'"
                size="small"
                variant="text"
                color="error"
                @click="confirmRemoveSpecificRole(item, 'ADMIN')"
                prepend-icon="mdi-account-minus"
              >
                Remove ADMIN
              </v-btn>
              
              <v-btn
                v-if="item.ActiveRole === 'Referee'"
                size="small"
                variant="text"
                color="error"
                @click="confirmRemoveSpecificRole(item, 'Referee')"
                prepend-icon="mdi-account-minus"
              >
                Remove Referee
              </v-btn>
              
              <!-- Fallback: Remove active role button -->
              <v-btn
                v-if="item.ActiveRole === 'N/A'"
                size="small"
                variant="text"
                color="error"
                @click="confirmRemoveRole(item)"
                prepend-icon="mdi-account-minus"
              >
                Remove Role
              </v-btn>
            </template>
            
            <v-btn
              size="small"
              variant="text"
              color="warning"
              @click="emergencyFixUser(item.UserID)"
              prepend-icon="mdi-wrench"
            >
              Fix
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Role Modal -->
    <v-dialog v-model="roleModal" max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-account-plus</v-icon>
          Add Role to User
        </v-card-title>
        <v-card-text>
          <v-form ref="roleForm" @submit.prevent="assignRole">
            <v-select
              label="User"
              v-model="selectedUserId"
              :items="availableUsers"
              item-title="fullName"
              item-value="id"
              required
              variant="outlined"
              prepend-inner-icon="mdi-account"
            />
            <v-select label="Role" v-model="selectedRole" :items="roleOptions"
              item-title="title" item-value="value" required variant="outlined" prepend-inner-icon="mdi-shield-account"/>
            <v-alert v-if="modalError" type="error" variant="tonal" class="mt-3">
              {{ modalError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="roleModal = false">Cancel</v-btn>
          <v-btn color="primary" @click="assignRole" :loading="roleLoading">Assign Role</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Role Confirmation -->
    <v-dialog v-model="removeRoleModal" max-width="400">
  <v-card>
    <v-card-title>
      <v-icon color="error" class="mr-2">mdi-account-minus</v-icon>
      Remove {{ selectedUser?.SpecificRoleToRemove || selectedUser?.ActiveRole || 'Active' }} Role
    </v-card-title>
    <v-card-text>
      <p>Are you sure you want to remove the <strong>{{ selectedUser?.SpecificRoleToRemove || selectedUser?.ActiveRole }}</strong> role from <strong>{{ selectedUser?.Email }}</strong>?</p>
      
      <!-- ✅ SHOW LOADING STATE -->
      <div v-if="roleLoading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" class="mb-2"></v-progress-circular>
        <p class="text-body-2">Removing role...</p>
      </div>
      
      <v-alert v-if="modalError" type="error" variant="tonal" class="mt-3">
        {{ modalError }}
      </v-alert>
      
      <v-alert v-if="!roleLoading" type="warning" variant="tonal" class="mt-3">
        <v-icon start>mdi-alert</v-icon>
        This action cannot be undone. The user will lose all permissions associated with this role.
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn 
        variant="text" 
        @click="closeRemoveRoleModal" 
        :disabled="roleLoading"
      >
        Cancel
      </v-btn>
      <v-btn 
        color="error" 
        @click="removeRole" 
        :loading="roleLoading"
        :disabled="roleLoading"
      >
        <v-icon start>mdi-delete</v-icon>
        Remove Role
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>


    <!-- Claims Details Dialog -->
    <v-dialog v-model="claimsModal" max-width="800">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-shield-account</v-icon>
          Firebase Claims Details
          <v-spacer></v-spacer>
          <v-chip 
            :color="claimsDetails?.claimsState === 'set' ? 'success' : 'error'"
            variant="flat"
            size="small"
          >
            {{ claimsDetails?.claimsState === 'set' ? 'Claims Set' : 'Claims Missing' }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined">
                <v-card-title class="text-h6">
                  <v-icon class="mr-2">mdi-account</v-icon>
                  User Information
                </v-card-title>
                <v-card-text>
                  <v-list density="compact">
                    <v-list-item>
                      <v-list-item-title>Name:</v-list-item-title>
                      <v-list-item-subtitle>{{ claimsDetails?.NameFirst }} {{ claimsDetails?.NameLast }}</v-list-item-subtitle></v-list-item>
                    <v-list-item>
                      <v-list-item-title>Email:</v-list-item-title>
                      <v-list-item-subtitle>{{ claimsDetails?.Email }}</v-list-item-subtitle></v-list-item>
                    <v-list-item>
                      <v-list-item-title>User ID:</v-list-item-title>
                      <v-list-item-subtitle class="text-caption">{{ claimsDetails?.UserID }}</v-list-item-subtitle></v-list-item>
                    <v-list-item>
                      <v-list-item-title>Organization:</v-list-item-title>
                      <v-list-item-subtitle>{{ claimsDetails?.Organization }}</v-list-item-subtitle></v-list-item>
                    <v-list-item>
                      <v-list-item-title>Active Role:</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip 
                          :color="getRoleColor(claimsDetails?.ActiveRole)"
                          size="small"
                          variant="flat"
                        >
                          {{ claimsDetails?.ActiveRole }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-card variant="outlined">
                <v-card-title class="text-h6">
                  <v-icon class="mr-2">mdi-shield-check</v-icon>
                  Claims Status
                </v-card-title>
                <v-card-text>
                  <v-list density="compact">
                    <v-list-item>
                      <v-list-item-title>Claims State:</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip 
                          :color="claimsDetails?.claimsState === 'set' ? 'success' : 'error'"
                          size="small"
                          variant="flat"
                          :prepend-icon="claimsDetails?.claimsState === 'set' ? 'mdi-check' : 'mdi-alert'"
                        >
                          {{ claimsDetails?.claimsState === 'set' ? 'Set' : 'Missing' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Role Source:</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip 
                          :color="claimsDetails?.RoleSource === 'claims' ? 'warning' : 'info'"
                          size="small"
                          variant="flat"
                        >
                          {{ claimsDetails?.RoleSource }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="claimsDetails?.LastLogin">
                      <v-list-item-title>Last Login:</v-list-item-title>
                      <v-list-item-subtitle>{{ claimsDetails?.LastLogin }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card variant="outlined">
                <v-card-title class="text-h6">
                  <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                  Available Claims
                </v-card-title>
                <v-card-text>
                  <div v-if="claimsDetails?.ClaimsList && claimsDetails.ClaimsList.length > 0" class="d-flex flex-wrap gap-2">
                    <v-chip
                      v-for="claim in claimsDetails.ClaimsList"
                      :key="claim"
                      size="small"
                      label
                      variant="outlined"
                      color="primary"
                      prepend-icon="mdi-shield-account-outline"
                    >
                      {{ claim }}
                    </v-chip>
                  </div>
                  <div v-else class="text-center py-4">
                    <v-icon color="grey" size="large">mdi-shield-off</v-icon>
                    <p class="text-grey mt-2">No claims found</p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <v-row class="mt-4">
            <v-col cols="12">
              <v-card variant="outlined">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2">mdi-database</v-icon>
                  Firestore Roles
                  <v-spacer></v-spacer>
                  <v-btn 
                    size="small" 
                    variant="outlined" 
                    @click="loadUserTokenClaims"
                    :loading="loadingTokenClaims"
                    prepend-icon="mdi-key"
                  >
                    Load Token Claims
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <div v-if="claimsDetails?.RolesList && claimsDetails.RolesList.length > 0" class="d-flex flex-wrap gap-2">
                    <v-chip
                      v-for="role in claimsDetails.RolesList"
                      :key="role"
                      size="small"
                      label
                      :variant="role === claimsDetails?.ActiveRole ? 'flat' : 'outlined'"
                      :color="role === claimsDetails?.ActiveRole ? 'success' : 'success'"
                      :prepend-icon="getRoleIcon(role)"
                      :style="role === claimsDetails?.ActiveRole ? '' : 'border: 2px solid green;'"
                    >
                      {{ role }}
                      <v-tooltip v-if="role === claimsDetails?.ActiveRole" activator="parent" text="Active Role"></v-tooltip>
                    </v-chip>
                  </div>
                  <div v-else class="text-center py-4">
                    <v-icon color="grey" size="large">mdi-account-off</v-icon>
                    <p class="text-grey mt-2">No roles found in Firestore</p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <v-row class="mt-4" v-if="tokenClaims">
            <v-col cols="12">
              <v-card variant="outlined" color="blue-grey">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="blue-grey">mdi-key-variant</v-icon>
                  Firebase Authentication Token Claims
                  <v-spacer></v-spacer>
                  <v-chip 
                    size="small" 
                    color="blue-grey" 
                    variant="flat"
                    prepend-icon="mdi-clock"
                  >
                    Loaded: {{ tokenClaimsLoadedAt }}
                  </v-chip>
                </v-card-title>
                <v-card-text>
                  <v-expansion-panels variant="accordion" multiple>
                    <!-- Standard Claims -->
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-h6">
                        <v-icon class="mr-2">mdi-account-details</v-icon>
                        Standard Claims
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <v-list density="compact">
                          <v-list-item v-if="tokenClaims.email">
                            <v-list-item-title>Email:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono">{{ tokenClaims.email }}</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.email_verified !== undefined">
                            <v-list-item-title>Email Verified:</v-list-item-title>
                            <v-list-item-subtitle>
                              <v-chip 
                                :color="tokenClaims.email_verified ? 'success' : 'error'" 
                                size="small" 
                                variant="flat"
                                :prepend-icon="tokenClaims.email_verified ? 'mdi-check' : 'mdi-close'"
                              >
                                {{ tokenClaims.email_verified ? 'Verified' : 'Not Verified' }}
                              </v-chip>
                            </v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.user_id">
                            <v-list-item-title>User ID:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono text-caption">{{ tokenClaims.user_id }}</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.iss">
                            <v-list-item-title>Issuer:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono text-caption">{{ tokenClaims.iss }}</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.aud">
                            <v-list-item-title>Audience:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono text-caption">{{ tokenClaims.aud }}</v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.iat">
                            <v-list-item-title>Issued At:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono text-caption">
                              {{ new Date(tokenClaims.iat * 1000).toLocaleString() }}
                            </v-list-item-subtitle>
                          </v-list-item>
                          <v-list-item v-if="tokenClaims.exp">
                            <v-list-item-title>Expires At:</v-list-item-title>
                            <v-list-item-subtitle class="font-mono text-caption">
                              {{ new Date(tokenClaims.exp * 1000).toLocaleString() }}
                            </v-list-item-subtitle>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-text>
                    </v-expansion-panel>

                    <!-- Custom Claims -->
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-h6">
                        <v-icon class="mr-2">mdi-shield-account</v-icon>
                        Custom Claims
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div v-if="hasCustomClaims">
                          <v-list density="compact">
                            <!-- Organization Roles (orgRoles) -->
                            <v-list-item v-if="tokenClaims.orgRoles">
                              <v-list-item-title>Organization Roles (orgRoles):</v-list-item-title>
                              <v-list-item-subtitle>
                                <div class="mt-2">
                                  <template v-for="(roles, org) in tokenClaims.orgRoles" :key="org">
                                    <v-chip 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined" 
                                      class="ma-1"
                                      prepend-icon="mdi-domain"
                                    >
                                      <strong>{{ org }}:</strong> {{ Array.isArray(roles) ? roles.join(', ') : roles }}
                                    </v-chip>
                                  </template>
                                </div>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Roles Array -->
                            <v-list-item v-if="tokenClaims.roles">
                              <v-list-item-title>Roles Array:</v-list-item-title>
                              <v-list-item-subtitle>
                                <div class="mt-2">
                                  <v-chip
                                    v-for="role in (Array.isArray(tokenClaims.roles) ? tokenClaims.roles : [tokenClaims.roles])"
                                    :key="role"
                                    size="small"
                                    color="success"
                                    variant="flat"
                                    class="ma-1"
                                    :prepend-icon="getRoleIcon(role)"
                                  >
                                    {{ role }}
                                  </v-chip>
                                </div>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Single Role -->
                            <v-list-item v-if="tokenClaims.role">
                              <v-list-item-title>Primary Role:</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip 
                                  size="small" 
                                  color="warning" 
                                  variant="flat"
                                  :prepend-icon="getRoleIcon(tokenClaims.role)"
                                >
                                  {{ tokenClaims.role }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Organization -->
                            <v-list-item v-if="tokenClaims.organization">
                              <v-list-item-title>Organization:</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip size="small" color="info" variant="flat" prepend-icon="mdi-domain">
                                  {{ tokenClaims.organization }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Old Roles Object -->
                            <v-list-item v-if="tokenClaims.roles && typeof tokenClaims.roles === 'object' && !Array.isArray(tokenClaims.roles)">
                              <v-list-item-title>Legacy Roles Object:</v-list-item-title>
                              <v-list-item-subtitle>
                                <div class="mt-2">
                                  <template v-for="(roleData, key) in tokenClaims.roles" :key="key">
                                    <v-chip 
                                      size="small" 
                                      color="orange" 
                                      variant="outlined" 
                                      class="ma-1"
                                    >
                                      {{ key }}: {{ roleData }}
                                    </v-chip>
                                  </template>
                                </div>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Boolean Role Claims -->
                            <template v-for="(value, key) in tokenClaims" :key="key">
                              <v-list-item v-if="isBooleanRoleClaim(key, value)">
                                <v-list-item-title>Role Claim ({{ key }}):</v-list-item-title>
                                <v-list-item-subtitle>
                                  <v-chip 
                                    size="small" 
                                    :color="value ? 'success' : 'error'" 
                                    variant="flat"
                                    :prepend-icon="value ? 'mdi-check' : 'mdi-close'"
                                  >
                                    {{ key }} = {{ value }}
                                  </v-chip>
                                </v-list-item-subtitle>
                              </v-list-item>
                            </template>

                            <!-- Claims Version -->
                            <v-list-item v-if="tokenClaims.claimsVersion">
                              <v-list-item-title>Claims Version:</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip size="small" color="blue" variant="flat" prepend-icon="mdi-tag">
                                  {{ tokenClaims.claimsVersion }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Last Updated -->
                            <v-list-item v-if="tokenClaims.lastUpdated">
                              <v-list-item-title>Last Updated:</v-list-item-title>
                              <v-list-item-subtitle class="font-mono text-caption">
                                {{ new Date(tokenClaims.lastUpdated).toLocaleString() }}
                              </v-list-item-subtitle>
                            </v-list-item>

                            <!-- Emergency Fix Flag -->
                            <v-list-item v-if="tokenClaims.emergencyFixed">
                              <v-list-item-title>Emergency Fixed:</v-list-item-title>
                              <v-list-item-subtitle>
                                <v-chip size="small" color="warning" variant="flat" prepend-icon="mdi-emergency">
                                  {{ tokenClaims.emergencyFixed }}
                                </v-chip>
                              </v-list-item-subtitle>
                            </v-list-item>
                          </v-list>
                        </div>
                        <div v-else class="text-center py-4">
                          <v-icon color="grey" size="large">mdi-shield-off</v-icon>
                          <p class="text-grey mt-2">No custom claims found</p>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>

                    <!-- Raw Claims -->
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-h6">
                        <v-icon class="mr-2">mdi-code-json</v-icon>
                        Raw Token Claims (JSON)
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <v-card variant="outlined" class="mt-2">
                          <v-card-text class="pa-0">
                            <pre class="token-raw-json">{{ JSON.stringify(tokenClaims, null, 2) }}</pre>
                          </v-card-text>
                        </v-card>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="claimsModal = false">Close</v-btn>
          <v-btn 
            color="primary" 
            @click="refreshUserClaims"
            :loading="refreshingClaims"
            prepend-icon="mdi-refresh"
          >
            Refresh Claims
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Debug Info -->
    <v-dialog v-model="debugModal" max-width="600">
      <v-card>
        <v-card-title>Authentication Debug Info</v-card-title>
        <v-card-text>
          <pre>{{ debugInfo }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="debugModal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import { getIdTokenResult, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, functions, auth } from "../firebase";
import { httpsCallable } from "firebase/functions";

const router = useRouter();

// Cloud Functions
const fnRemoveRole = httpsCallable(functions, "removeRoleWithTokenSync");
const fnDeleteUser = httpsCallable(functions, "adminDeleteUser");
const fnBackfillClaims = httpsCallable(functions, "adminBackfillClaims");
const fnSetUserActiveRole = httpsCallable(functions, "setUserActiveRole");
const fnDebugAuth = httpsCallable(functions, "getUserAuthStatus");
const fnEmergencyFix = httpsCallable(functions, "emergencyFixUserClaims");

// Data
const currentUserOrgName = ref("");
const rows = ref([]);
const tableLoading = ref(false);
const pageLoading = ref(true);
const roleModal = ref(false);
const removeRoleModal = ref(false);
const debugModal = ref(false);
const roleLoading = ref(false);
const modalError = ref("");
const debugInfo = ref("");

// Modal data
const selectedUserId = ref("");
const selectedRole = ref("");
const selectedUser = ref(null);

const successMessage = ref("");
const showSuccess = ref(false);

const modalSuccess = ref("");
const showSnackbar = ref(false);
const snackbarMessage = ref("");
const fixingClaims = ref(false);
const emergencyFixing = ref(false);
const fixingUser = ref(null);
const snackbarColor = ref("success");

// Claims dialog data
const claimsModal = ref(false);
const claimsDetails = ref(null);
const refreshingClaims = ref(false);

// Token claims data
const tokenClaims = ref(null);
const tokenClaimsLoadedAt = ref("");
const loadingTokenClaims = ref(false);

// Computed
const availableUsers = computed(() => {
  return rows.value.map(user => ({
    id: user.UserID,
    fullName: [user.NameFirst, user.NameLast].filter(Boolean).join(' ') || user.Email || user.UserID,
    email: user.Email,
    currentRoles: user.AllRoles
  })).sort((a, b) => a.fullName.localeCompare(b.fullName));
});

const roleOptions = [
  { title: "Referee", value: "Referee" },
  { title: "Referee Coach", value: "RefereeCoach" },
  { title: "Administrator", value: "ADMIN" }
];

// Data table headers
const headers = [
  { title: "User", key: "NameLast", sortable: true },
  { title: "Roles", key: "Roles", sortable: false },
  { title: "Last Login", key: "LastLogin", sortable: true },
  { title: "Claims", key: "claims", sortable: false },
  { title: "Actions", key: "actions", sortable: false, width: 300 }
];

// Program code when page opens
// ================================================
onMounted(() => {
  console.log('Maintain_Users component mounted .............');
  
  // Set up auth state listener
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user) {
      console.log("✅ Auth state changed - User is authenticated:", user.email);
      
      try {
        // Get user claims and organization
        const { claims } = await getIdTokenResult(user);
        console.log("🔑 User claims:", claims);
        currentUserOrgName.value = claims?.org || localStorage.getItem("currentUserOrgName") || "";
        console.log(`🏢 Organization: ${currentUserOrgName.value}`);
        
        // Load users for this organization
        await loadUsers();
        
      } catch (e) {
        console.error("❌ Error loading user data:", e);
      } finally {
        pageLoading.value = false;
        tableLoading.value = false;
      }
      
    } else {
      console.log("❌ Auth state changed - User is NOT authenticated");
      pageLoading.value = false;
      tableLoading.value = false;
      router.push("/login");
    }
  });
  
  // Cleanup listener when component unmounts
  onUnmounted(() => {
    console.log('🧹 Cleaning up auth listener');
    unsubscribe();
  });
});

async function loadUsers() {
  tableLoading.value = true;
  try {
    rows.value = [];
    
    if (!currentUserOrgName.value) {
      console.log('❌ No organization set');
      return;
    }

    // Get all users from Firestore
    const snap = await getDocs(collection(db, "users"));
    const usersList = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
    
    const tmp = [];
    
    for (const u of usersList) {
      try {
        const rInOrg = rolesForOrg(u, currentUserOrgName.value);    // Get user's roles for this organization
        const lastLoginStr = fmtLastLogin(u.LastLogin);              // Format last login date

        // Determine claims state
        const activeRoleEntry = rInOrg.find(r => r.Role === u.ActiveRole) || rInOrg[0];
        const claimsState = claimsStateFor(u, activeRoleEntry, currentUserOrgName.value);

        // Create row data
        const rowData = {
          UserID: u.id,
          ActiveRole: u.ActiveRole || rInOrg[0]?.Role || "N/A",
          NameFirst: u.NameFirst || "",
          NameLast: u.NameLast || "",
          Email: u.email || u.id,
          LastLogin: lastLoginStr,
          Organization: u.Organization || currentUserOrgName.value,
          Roles: u.Roles || {},
          ClaimsState: claimsState,
          ClaimsText: claimsState === 'set' ? '✓ Set' : '⚠ Missing',
          rowKey: u.id,
          RolesCount: rInOrg.length,
          HasRoles: rInOrg.length > 0,
          AllRoles: getAllRolesForOrg(u, currentUserOrgName.value),
          RolesList: rInOrg.map(r => r.Role),
          ClaimsList: getClaimsListForUser(u, currentUserOrgName.value),
          RoleSource: rInOrg.length === 0 && u.ActiveRole ? 'claims' : 'firestore'
        };

        tmp.push(rowData);
        
      } catch (userError) {
        console.error(`   ❌ Error processing user ${u.email}:`, userError);
        // Still add the user even if there's an error
        tmp.push({
          UserID: u.id,
          ActiveRole: "Error",
          NameFirst: u.NameFirst || "",
          NameLast: u.NameLast || "",
          Email: u.email || u.id,
          LastLogin: "Error",
          Organization: u.Organization || currentUserOrgName.value,
          Roles: u.Roles || {},
          ClaimsState: 'na',
          ClaimsText: 'Error',
          rowKey: u.id,
          RolesCount: 0,
          HasRoles: false,
          AllRoles: 'Error loading roles',
          RolesList: [],
          ClaimsList: []
        });
      }
    }

    // Sort users - those with roles first, then by name
    tmp.sort((a, b) => {
      if (a.HasRoles !== b.HasRoles) {
        return b.HasRoles - a.HasRoles;
      }
      const order = { ADMIN: 1, RefereeCoach: 2, Referee: 3, "N/A": 4, Error: 5 };
      const ra = order[a.ActiveRole] || 99;
      const rb = order[b.ActiveRole] || 99;
      if (ra !== rb) return ra - rb;
      return (a.NameLast || '').localeCompare(b.NameLast || '');
    });

    rows.value = tmp;
    
    // Log final result for verification
    console.log('📊 Users in table:', rows.value.map(r => ({
      name: `${r.NameFirst} ${r.NameLast}`,
      email: r.Email,
      activeRole: r.ActiveRole,
      hasRoles: r.HasRoles
    })));
    
  } catch (e) {
    console.error("❌ CRITICAL loadUsers error:", e);
    // Show error in table
    rows.value = [{
      UserID: 'error',
      ActiveRole: 'Error',
      NameFirst: 'System',
      NameLast: 'Error',
      Email: e.message,
      LastLogin: 'Never',
      Organization: currentUserOrgName.value,
      Roles: {},
      ClaimsState: 'na',
      ClaimsText: 'Load Error',
      rowKey: 'error',
      RolesCount: 0,
      HasRoles: false,
      AllRoles: e.message
    }];
  } finally {
    tableLoading.value = false;
  }
}

function rolesForOrg(userData, org) {
  if (!userData) {
    console.warn("userData is undefined in rolesForOrg");
    return [];
  }
  
  if (!userData?.Roles) return [];
  
  const roles = userData.Roles;
  
  // Handle array structure
  if (Array.isArray(roles)) {
    return roles.filter(r => 
      (r?.Organization || r?.OrgName || r?.organization) === org && 
      !!r?.Role
    );
  }
  
  // Handle map structure (this is what you need!)
  return Object.values(roles).filter(role => 
    (role?.Organization || role?.OrgName || role?.organization) === org && 
    role?.Role
  );
}

// Check claims state for a role
function claimsStateFor(userDoc, roleEntry, org) {
  // ADD THIS CHECK AT THE START
  if (!userDoc) {
    console.warn("userDoc is undefined in claimsStateFor");
    return 'na';
  }
  
  const role = roleEntry?.Role || '';
  if (!role) return 'missing';

  const back = userDoc?.ClaimsBackfill || {};
  const last = userDoc?.LastClaimsBackfillAt;

  // Check if claims match the current active role
  const matchesActiveRole = userDoc?.ActiveRole === role;
  
  // Or if claims were backfilled for this role
  const matchesBackfill =
    (Array.isArray(back.roles) && back.roles.some(x =>
      (x?.role || x?.Role) === role &&
      ((x?.organization || x?.org) === org)))
    ||
    (((back?.role || back?.Role) === role) &&
     ((back?.organization || back?.org) === org));

  // Check if user was assigned this role in Firestore
  const hasRoleInFirestore = rolesForOrg(userDoc, org).some(r => r.Role === role);

  const has = last || matchesBackfill || matchesActiveRole || hasRoleInFirestore;
  return has ? 'set' : 'missing';
}

// Get all roles as a string for display
function getAllRolesForOrg(userData, org) {
  const roles = rolesForOrg(userData, org);
  if (roles.length === 0) return 'No roles assigned';
  return roles.map(r => r.Role).join(', ');
}

// Get claims list for user
function getClaimsListForUser(userData, org) {
  if (!userData) return [];
  
  // Try to get claims from user data
  const claims = [];
  
  // Check if user has role claim
  if (userData.ActiveRole) {
    claims.push(userData.ActiveRole);
  }
  
  // Check if user has organization claim
  if (userData.Organization) {
    claims.push(`Org: ${userData.Organization}`);
  }
  
  // Get roles from Firestore
  const roles = rolesForOrg(userData, org);
  roles.forEach(r => {
    if (r.Role && !claims.includes(r.Role)) {
      claims.push(r.Role);
    }
  });
  
  return claims.length > 0 ? claims : ['No claims'];
}

// Format last login
function fmtLastLogin(lastLogin) {
  if (!lastLogin) return 'Never';
  
  try {
    const date = typeof lastLogin.toDate === 'function' 
      ? lastLogin.toDate()
      : new Date(lastLogin.seconds ? lastLogin.seconds * 1000 : lastLogin);
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  } catch {
    return 'Invalid';
  }
}

// Actions Code: "Add Role"
// ================================================
function openRoleModalForUser(user) {
  selectedUserId.value = user.UserID;
  selectedRole.value = "";
  modalError.value = "";
  roleModal.value = true;
}

const fnAssignRole = httpsCallable(functions, "assignRoleWithTokenSync");

async function assignRole() {
  if (!selectedUserId.value || !selectedRole.value) {
    modalError.value = "Please select both a user and a role.";
    return;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    modalError.value = "You must be logged in to assign roles.";
    return;
  }

  try {
    // Force refresh the auth token
    await currentUser.getIdToken(true);
    console.log('✅ Token refreshed for user:', currentUser.email);
  } catch (error) {
    modalError.value = "Authentication error. Please sign in again.";
    return;
  }

  roleLoading.value = true;
  modalError.value = "";
  modalSuccess.value = "";

  try {
    console.log(`🎯 Assigning ${selectedRole.value} to ${selectedUserId.value} using Cloud Function`);
    
    // Call the Cloud Function that updates BOTH Firestore AND Claims
    const result = await fnAssignRole({
      targetUserId: selectedUserId.value,
      role: selectedRole.value,
      organization: currentUserOrgName.value,
      assignedBy: auth.currentUser?.email || 'admin'
    });

    if (result.data?.success) {
      console.log('✅ Role assigned successfully:', result.data);
      
      modalSuccess.value = `${selectedRole.value} role assigned successfully! Both Firestore and Auth claims have been updated.`;
      showNotification(`Successfully assigned ${selectedRole.value} role`, 'success');
      
      // Close modal after short delay
      setTimeout(() => {
        roleModal.value = false;
        selectedUserId.value = "";
        selectedRole.value = "";
        modalSuccess.value = "";
      }, 2000);
      
      // Refresh the user list
      await loadUsers();
      
    } else {
      modalError.value = result.data?.error || "Failed to assign role";
      showNotification('Failed to assign role: ' + modalError.value, 'error');
    }
  } catch (error) {
    console.error('❌ Assign role error:', error);
    modalError.value = error.message || "Failed to assign role";
    showNotification('Error assigning role: ' + error.message, 'error');
  } finally {
    roleLoading.value = false;
  }
}

async function testCloudFunctionAuth() {
  try {
    console.log('🔍 Testing Cloud Function authentication...');
    
    // First, check current user auth state
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('❌ No current user found');
      return;
    }
    
    console.log('✅ Current user:', currentUser.email);
    
    // Get token to verify claims
    const tokenResult = await currentUser.getIdTokenResult(true);
    console.log('🔑 Token claims:', tokenResult.claims);
    
    // Test the debug Cloud Function
    const testDebug = httpsCallable(functions, 'testAuthContext');
    const result = await testDebug({});
    
    console.log('📊 Cloud Function auth result:', result.data);
    
    if (!result.data.hasAuth) {
      console.error('❌ Cloud Function received no auth context');
      alert('Authentication context not reaching Cloud Functions. Check Firebase configuration.');
    } else {
      console.log('✅ Cloud Function authentication working!');
      alert('Cloud Function authentication is working correctly.');
    }
    
  } catch (error) {
    console.error('❌ Auth test failed:', error);
    alert(`Auth test failed: ${error.message}`);
  }
}

//===============================================

// Get role color
function getRoleColor(role) {
  const colors = {
    'ADMIN': 'error',
    'RefereeCoach': 'success',
    'Referee': 'primary'
  };
  return colors[role] || 'grey';
}

// Get role icon
function getRoleIcon(role) {
  const icons = {
    'ADMIN': 'mdi-shield-account',
    'RefereeCoach': 'mdi-account-star',
    'Referee': 'mdi-whistle'
  };
  return icons[role] || 'mdi-account';
}

// Remove role from user
async function removeRole() {
  if (!selectedUser.value) return;

  roleLoading.value = true;
  modalError.value = "";
  let shouldCloseDialog = false; // ✅ Track if we should close dialog

  try {
    const user = selectedUser.value;
    const roleToRemove = user.SpecificRoleToRemove || 
      (user.ActiveRole !== "N/A" ? user.ActiveRole : null);

    if (!roleToRemove) {
      modalError.value = "No role specified for removal.";
      return; // Don't close dialog on validation error
    }

    console.log(`🗑️ Removing specific role "${roleToRemove}" from user ${user.Email}`);
    const result = await removeRoleDirectly(user.UserID, roleToRemove, currentUserOrgName.value);

    if (result.success) {
      console.log('✅ Role removed successfully:', result);
      shouldCloseDialog = true; // ✅ Mark for closing
      
      // Show success message
      showSuccessMessage(`Successfully removed ${roleToRemove} role from ${user.Email}`);
      
      // Refresh the table
      console.log('🔄 Refreshing user table...');
      await loadUsers();
      
    } else {
      modalError.value = result.error || "Failed to remove role";
      // Don't close dialog on error
    }
  } catch (error) {
    console.error('Remove role error:', error);
    modalError.value = error.message || "Failed to remove role";
    // Don't close dialog on error
  } finally {
    roleLoading.value = false;
    
    // ✅ CLOSE DIALOG IF SUCCESSFUL
    if (shouldCloseDialog) {
      removeRoleModal.value = false;
      selectedUser.value = null;
      console.log('✅ Dialog closed successfully');
    }
  }
}

// Actions Code: "Remove Role"
// ================================================

async function removeRoleDirectly(userId, role, organization) {
  try {
    console.log(`🔧 Removing role ${role} for user ${userId} in org ${organization}`);
    
    // Step 1: Check Firestore first
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, error: "User not found" };
    }
    
    const userData = userSnap.data();
    const currentRoles = userData.Roles || {};
    
    console.log(`📋 Firestore Roles:`, currentRoles);
    console.log(`🎯 Active Role from userData: ${userData.ActiveRole}`);
    
    let firestoreRoleRemoved = false;
    let updatedRoles = { ...currentRoles };
    
    // Step 2: Remove from Firestore if it exists
    Object.keys(currentRoles).forEach(key => {
      const roleEntry = currentRoles[key];
      const roleOrg = roleEntry.Organization || roleEntry.OrgName || roleEntry.organization;
      
      if (roleEntry.Role === role && roleOrg === organization) {
        console.log(`🗑️ Removing from Firestore:`, roleEntry);
        delete updatedRoles[key];
        firestoreRoleRemoved = true;
      }
    });
    
    // Step 3: Update Firestore
    const updateData = {
      Roles: updatedRoles,
      updatedAt: serverTimestamp()
    };
    
    // If user's active role is being removed, clear it
    if (userData.ActiveRole === role) {
      updateData.ActiveRole = null;
      console.log(`🔄 Clearing active role ${role}`);
    }
    
    await updateDoc(userRef, updateData);
    console.log(`✅ Firestore updated`);
    
    // Step 4: If role wasn't in Firestore, that's okay (claims sync issue)
    if (!firestoreRoleRemoved) {
      console.log(`ℹ️ Role ${role} was not in Firestore (only in claims) - this is normal for token sync issues`);
    }
    
    return { 
      success: true, 
      message: `Role ${role} removed successfully${firestoreRoleRemoved ? '' : ' (claims-only role)'}` 
    };
    
  } catch (error) {
    console.error('Role removal error:', error);
    return { success: false, error: error.message };
  }
}

// Emergency fix for user claims
async function emergencyFixUser(userId) {
  try {
    const result = await fnEmergencyFix({
      targetUserId: userId,
      organization: currentUserOrgName.value,
      forceCompleteRefresh: true
    });

    if (result.data?.success) {
      console.log('Emergency fix completed:', result.data);
      await loadUsers(); // Refresh the list
    } else {
      console.error('Emergency fix failed:', result.data);
    }
  } catch (error) {
    console.error('Emergency fix error:', error);
  }
}

// Fix all user claims to standardize them in proper array format
async function fixAllUserClaims() {
  fixingClaims.value = true;
  try {
    console.log('🔧 Starting to fix all user claims...');
    
    if (!currentUserOrgName.value) {
      showNotification('No organization set', 'error');
      return;
    }

    // Get all users from Firestore
    const snap = await getDocs(collection(db, "users"));
    const usersList = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
    
    let fixedCount = 0;
    let errorCount = 0;
    const results = [];

    for (const user of usersList) {
      try {
        // Get user's roles for this organization
        const userRoles = rolesForOrg(user, currentUserOrgName.value);
        
        if (userRoles.length === 0) {
          console.log(`⏭️ Skipping user ${user.email} - no roles found`);
          continue;
        }

        console.log(`🔧 Processing user: ${user.email}, Roles: ${userRoles.map(r => r.Role).join(', ')}`);

        // Use the existing backfill claims function to fix this user
        const result = await fnBackfillClaims({
          targetUserId: user.id,
          organization: currentUserOrgName.value,
          forceRefresh: true,
          standardizeClaims: true // Add this flag to ensure claims are set as arrays
        });

        if (result.data?.success) {
          fixedCount++;
          results.push({
            userId: user.id,
            email: user.email,
            roles: userRoles.map(r => r.Role),
            status: 'success',
            message: result.data.message || 'Claims fixed successfully'
          });
          console.log(`✅ Fixed claims for ${user.email}`);
        } else {
          errorCount++;
          results.push({
            userId: user.id,
            email: user.email,
            status: 'error',
            message: result.data?.error || 'Unknown error'
          });
          console.error(`❌ Failed to fix claims for ${user.email}:`, result.data);
        }

        // Small delay to avoid overwhelming Firebase
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (userError) {
        errorCount++;
        results.push({
          userId: user.id,
          email: user.email || user.id,
          status: 'error',
          message: userError.message
        });
        console.error(`❌ Error processing user ${user.email}:`, userError);
      }
    }

    console.log(`🔧 Claims fixing complete: ${fixedCount} fixed, ${errorCount} errors`);
    
    // Show summary
    showNotification(
      `Claims fixing complete: ${fixedCount} users fixed, ${errorCount} errors. Check console for details.`,
      errorCount === 0 ? 'success' : 'warning'
    );

    // Log detailed results
    console.group('📊 Detailed Results:');
    results.forEach(result => {
      console.log(`${result.status === 'success' ? '✅' : '❌'} ${result.email}: ${result.message}`);
    });
    console.groupEnd();

    // Refresh the user list
    await loadUsers();

  } catch (error) {
    console.error('🔧 Critical error in fixAllUserClaims:', error);
    showNotification('Failed to fix claims: ' + error.message, 'error');
  } finally {
    fixingClaims.value = false;
  }
}

// Emergency fix all user claims using the emergency fix function
async function emergencyFixAllClaims() {
  emergencyFixing.value = true;
  try {
    console.log('🚨 Starting emergency fix for all user claims...');
    
    if (!currentUserOrgName.value) {
      showNotification('No organization set', 'error');
      return;
    }

    // Get all users from Firestore
    const snap = await getDocs(collection(db, "users"));
    const usersList = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
    
    let fixedCount = 0;
    let errorCount = 0;

    for (const user of usersList) {
      try {
        console.log(`🚨 Emergency fixing user: ${user.email || user.id}`);

        const result = await fnEmergencyFix({
          targetUserId: user.id,
          organization: currentUserOrgName.value,
          forceCompleteRefresh: true
        });

        if (result.data?.success) {
          fixedCount++;
          console.log(`✅ Emergency fix completed for ${user.email || user.id}`);
        } else {
          errorCount++;
          console.error(`❌ Emergency fix failed for ${user.email || user.id}:`, result.data);
        }

        // Small delay to avoid overwhelming Firebase
        await new Promise(resolve => setTimeout(resolve, 200));

      } catch (userError) {
        errorCount++;
        console.error(`❌ Emergency fix error for ${user.email || user.id}:`, userError);
      }
    }

    console.log(`🚨 Emergency fixing complete: ${fixedCount} fixed, ${errorCount} errors`);
    
    showNotification(
      `Emergency fix complete: ${fixedCount} users fixed, ${errorCount} errors.`,
      errorCount === 0 ? 'success' : 'warning'
    );

    // Refresh the user list
    await loadUsers();

  } catch (error) {
    console.error('🚨 Critical error in emergencyFixAllClaims:', error);
    showNotification('Emergency fix failed: ' + error.message, 'error');
  } finally {
    emergencyFixing.value = false;
  }
}

// Handle row click to fix individual user claims
async function handleRowClick(event, { item }) {
  // Don't trigger if clicking on buttons or interactive elements
  if (event.target.closest('button, .v-btn, a, .v-checkbox, .v-select')) {
    return;
  }

  fixingUser.value = item.UserID;
  
  try {
    console.log(`🔧 Fixing claims for user: ${item.Email} (${item.UserID})`);
    
    const result = await fnEmergencyFix({
      targetUserId: item.UserID,
      organization: currentUserOrgName.value,
      forceCompleteRefresh: true
    });

    if (result.data?.success) {
      console.log(`✅ Claims fixed for ${item.Email}:`, result.data);
      showNotification(
        `Claims fixed for ${item.NameFirst} ${item.NameLast}. Roles: ${result.data.data?.rolesArray?.join(', ') || 'N/A'}`,
        'success'
      );
      
      // Refresh the user list
      await loadUsers();
      
    } else {
      console.error(`❌ Failed to fix claims for ${item.Email}:`, result.data);
      showNotification(
        `Failed to fix claims for ${item.NameFirst} ${item.NameLast}: ${result.data?.error || 'Unknown error'}`,
        'error'
      );
    }

  } catch (error) {
    console.error(`❌ Error fixing claims for ${item.Email}:`, error);
    showNotification(
      `Error fixing claims for ${item.NameFirst} ${item.NameLast}: ${error.message}`,
      'error'
    );
  } finally {
    fixingUser.value = null;
  }
}

function confirmRemoveRole(user) {
  selectedUser.value = user;
  removeRoleModal.value = true;
}

function confirmRemoveSpecificRole(user, specificRole) {
  selectedUser.value = {
    ...user,
    SpecificRoleToRemove: specificRole
  };
  removeRoleModal.value = true;
}

function showSuccessMessage(message) {
  successMessage.value = message;  // ✅ Now this will work
  showSuccess.value = true;         // ✅ Now this will work
  
  // Auto-hide after 3 seconds
  setTimeout(() => {
    showSuccess.value = false;
    successMessage.value = "";
  }, 3000);
}

function closeRemoveRoleModal() {
  removeRoleModal.value = false;
  selectedUser.value = null;
  modalError.value = "";
  console.log('🚫 Role removal dialog cancelled');
}

async function syncClaimsToFirestore(userId) {
  try {
    console.log('🔄 Syncing claims to Firestore for user:', userId);
    
    // This would use a Cloud Function to properly sync claims
    // For now, just show info message
    alert('This feature requires Cloud Functions deployment. The role exists in Firebase Auth claims but not in Firestore.');
    
  } catch (error) {
    console.error('Sync error:', error);
  }
}
function showNotification(message, color = 'success') {
  snackbarMessage.value = message;
  snackbarColor.value = color;
  showSnackbar.value = true;
}

// Show claims dialog for user
async function showClaimsDialog(user) {
  claimsDetails.value = user;
  claimsModal.value = true;
}

// Refresh user claims from Firebase Auth
async function refreshUserClaims() {
  if (!claimsDetails.value?.UserID) return;
  
  refreshingClaims.value = true;
  try {
    console.log(`🔄 Refreshing claims for user: ${claimsDetails.value.Email}`);
    
    // Use the emergency fix function to refresh claims
    const result = await fnEmergencyFix({
      targetUserId: claimsDetails.value.UserID,
      organization: currentUserOrgName.value,
      forceCompleteRefresh: true
    });

    if (result.data?.success) {
      console.log('✅ Claims refreshed successfully:', result.data);
      showNotification('Claims refreshed successfully', 'success');
      
      // Refresh the user data and reload the table
      await loadUsers();
      
      // Update the dialog with fresh data
      const updatedUser = rows.value.find(r => r.UserID === claimsDetails.value.UserID);
      if (updatedUser) {
        claimsDetails.value = updatedUser;
      }
      
    } else {
      console.error('❌ Failed to refresh claims:', result.data);
      showNotification('Failed to refresh claims: ' + (result.data?.error || 'Unknown error'), 'error');
    }
    
  } catch (error) {
    console.error('❌ Error refreshing claims:', error);
    showNotification('Error refreshing claims: ' + error.message, 'error');
  } finally {
    refreshingClaims.value = false;
  }
}

// Load user's Firebase token claims
async function loadUserTokenClaims() {
  if (!claimsDetails.value?.UserID) return;
  
  loadingTokenClaims.value = true;
  try {
    console.log(`🔑 Loading token claims for user: ${claimsDetails.value.Email}`);
    
    // Use cloud function to get user's token claims
    const fnGetTokenClaims = httpsCallable(functions, "getUserTokenClaims");
    const result = await fnGetTokenClaims({
      targetUserId: claimsDetails.value.UserID
    });

    if (result.data?.success && result.data?.claims) {
      tokenClaims.value = result.data.claims;
      tokenClaimsLoadedAt.value = new Date().toLocaleTimeString();
      console.log('✅ Token claims loaded:', tokenClaims.value);
      showNotification('Token claims loaded successfully', 'success');
    } else {
      console.error('❌ Failed to load token claims:', result.data);
      showNotification('Failed to load token claims: ' + (result.data?.error || 'Unknown error'), 'error');
    }
    
  } catch (error) {
    console.error('❌ Error loading token claims:', error);
    showNotification('Error loading token claims: ' + error.message, 'error');
  } finally {
    loadingTokenClaims.value = false;
  }
}

// Check if user has any custom claims
const hasCustomClaims = computed(() => {
  if (!tokenClaims.value) return false;
  
  const customClaimKeys = ['orgRoles', 'roles', 'role', 'organization', 'claimsVersion', 'lastUpdated', 'emergencyFixed'];
  return customClaimKeys.some(key => tokenClaims.value[key] !== undefined) ||
         Object.keys(tokenClaims.value).some(key => isBooleanRoleClaim(key, tokenClaims.value[key]));
});

// Check if a claim is a boolean role claim (like ADMIN: true, Referee: true, etc.)
function isBooleanRoleClaim(key, value) {
  const booleanRoleKeys = ['ADMIN', 'Referee', 'RefereeCoach', 'Player', 'Spectator'];
  return booleanRoleKeys.includes(key) && typeof value === 'boolean';
}

</script>

<style scoped>
.v-data-table {
  font-size: 0.875rem;
}

.text-medium-emphasis {
  opacity: 0.7;
}

.gap-1 {
  gap: 4px;
}

.clickable-claim {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-claim:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.token-raw-json {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>